// app/javascript/controllers/wiki_search_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = ["input", "item", "section", "clearButton"]

    connect() {
        this.debounceTimer = null
    }

    disconnect() {
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer)
        }
    }

    filter() {
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer)
        }

        this.debounceTimer = setTimeout(() => {
            this.performFilter()
        }, 150)
    }

    clear() {
        this.inputTarget.value = ''
        this.performFilter()
        this.inputTarget.focus()
    }

    restoreCollapseState() {
        // Restore <details> open/closed state from localStorage (wiki_tree_${id}).
        // Missing entries → leave state as-is (preserves server-rendered auto-expand).
        this.element.querySelectorAll('details.wiki-tree-disclosure').forEach(details => {
            const id = details.dataset.wikiTreeIdValue
            if (!id) return
            const stored = localStorage.getItem(`wiki_tree_${id}`)
            if (stored === 'true') details.open = true
            else if (stored === 'false') details.open = false
        })
    }

    performFilter() {
        const query = this.inputTarget.value.toLowerCase().trim()

        if (this.hasClearButtonTarget) {
            this.clearButtonTarget.classList.toggle('hidden', !query)
        }

        if (!query) {
            this.itemTargets.forEach(item => {
                item.classList.remove('hidden')
                this.unhighlightText(item)
            })
            this.sectionTargets.forEach(section => {
                section.classList.remove('hidden')
            })
            this.restoreCollapseState()
            return
        }

        // Hide all items and sections first
        this.itemTargets.forEach(item => item.classList.add('hidden'))
        this.sectionTargets.forEach(section => section.classList.add('hidden'))

        // Find matching items and reveal them + their ancestors
        const matchingItems = this.itemTargets.filter(item => {
            const title = item.dataset.wikiSearchTitle?.toLowerCase() || ''
            return title.includes(query)
        })

        matchingItems.forEach(item => {
            item.classList.remove('hidden')
            this.highlightText(item, query)
            this.showParentSections(item)
        })
    }

    showParentSections(item) {
        let parent = item.parentElement
        while (parent && parent !== this.element) {
            const targets = parent.dataset.wikiSearchTarget?.split(/\s+/) || []
            if (targets.includes('section')) {
                parent.classList.remove('hidden')
                const disclosure = parent.querySelector(':scope > details.wiki-tree-disclosure')
                if (disclosure) disclosure.open = true
            }
            parent = parent.parentElement
        }
    }

    highlightText(item, query) {
        const titleElement = item.querySelector('[data-wiki-search-title-text]')
        if (!titleElement) return

        const originalText = titleElement.dataset.originalText || titleElement.textContent
        titleElement.dataset.originalText = originalText

        const regex = new RegExp(`(${this.escapeRegExp(query)})`, 'gi')
        titleElement.innerHTML = originalText.replace(regex, '<mark class="bg-warning-200 rounded px-0.5">$1</mark>')
    }

    unhighlightText(item) {
        const titleElement = item.querySelector('[data-wiki-search-title-text]')
        if (!titleElement || !titleElement.dataset.originalText) return

        titleElement.textContent = titleElement.dataset.originalText
    }

    escapeRegExp(string) {
        return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    }
}
